import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useRoles, hasRole } from '@/lib/useRoles';
import Button from '@/components/base/Button';
import Card from '@/components/base/Card';

interface Status {
  id: string;
  key: string;
  title: string;
}
interface Rule {
  step_id: string;
  next_step_ids: string[] | null;
  authors: string[] | null;
}
interface EventRow {
  id: string;
  event_type: string;
  event_value: string | null;
  done_at: string;
  done_by: string | null;
}
interface LoadRow {
  id: string;
  order_no: string;
  status_id: string | null;
  status: Status | Status[] | null;
  [k: string]: unknown;
}

const eventLabels: Record<string, string> = {
  step_transition: 'Mudança de estado',
  comment: 'Comentário',
  document_added: 'Documento adicionado',
  field_updated: 'Campo alterado',
  weight_start: 'Peso de entrada',
  weight_end: 'Peso de saída',
  weight_net: 'Peso líquido',
  pager: 'Pager',
  phone: 'Telefone',
  seal: 'Selo',
  washing_arrival: 'Entrada na lavagem',
  washing_departure: 'Saída da lavagem',
  rfid: 'RFID',
  sms: 'SMS',
  alert: 'Alerta',
};

interface FieldDef {
  key: string;
  label: string;
  type: 'text' | 'date' | 'time' | 'number' | 'array' | 'uuid';
}

// campos editáveis (lista alinhada com o allowlist do RPC)
const editableFields: FieldDef[] = [
  { key: 'phone', label: 'Telefone', type: 'text' },
  { key: 'seal', label: 'Selo', type: 'text' },
  { key: 'pager', label: 'Pager', type: 'number' },
  { key: 'packaging_type', label: 'Tipo de embalagem', type: 'text' },
  { key: 'client', label: 'Cliente', type: 'text' },
  { key: 'destination', label: 'Destino', type: 'text' },
  { key: 'loading_local', label: 'Local de carga', type: 'text' },
  { key: 'time_slot', label: 'Fração horária', type: 'text' },
  { key: 'washing', label: 'Lavagem', type: 'text' },
  { key: 'washing_time_min', label: 'Tempo de lavagem (min)', type: 'number' },
  { key: 'start_weight', label: 'Peso entrada', type: 'number' },
  { key: 'end_weight', label: 'Peso saída', type: 'number' },
  { key: 'load_weight', label: 'Peso líquido', type: 'number' },
  { key: 'notes', label: 'Notas', type: 'text' },
  { key: 'driver_id', label: 'Motorista (ID)', type: 'uuid' },
  { key: 'tractor_id', label: 'Trator (ID)', type: 'uuid' },
  { key: 'trailer_id', label: 'Reboque (ID)', type: 'uuid' },
  { key: 'email', label: 'Email', type: 'text' },
  { key: 'lang', label: 'Idioma', type: 'text' },
  { key: 'lot', label: 'Lotes', type: 'array' },
  { key: 'load_date', label: 'Data de carga', type: 'date' },
  { key: 'unload_date', label: 'Data descarga', type: 'date' },
  { key: 'delivery_date', label: 'Data entrega', type: 'date' },
  { key: 'delivery_arrival_hour', label: 'Chegada entrega', type: 'time' },
  { key: 'delivery_leave_hour', label: 'Saída entrega', type: 'time' },
];

// campos só-leitura (SAP/bloqueados/internos)
const readonlyFields: { key: string; label: string }[] = [
  { key: 'order_no', label: 'N.º Carga' },
  { key: 'grade', label: 'Grade' },
  { key: 'client_id', label: 'Cliente (ID)' },
  { key: 'doc_entry', label: 'DocEntry' },
  { key: 'u_ovdocentry', label: 'OV DocEntry' },
  { key: 'u_ovlinenum', label: 'OV Linha' },
  { key: 'u_agrupado', label: 'Agrupado' },
  { key: 'delivery_note', label: 'Delivery Note' },
  { key: 'delivery_request', label: 'Delivery Request' },
];

function titleOf(value: Status | Status[] | null | undefined) {
  if (!value) return null;
  const v = Array.isArray(value) ? value[0] : value;
  return v?.title ?? null;
}

export default function LoadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { roles } = useRoles();

  const [load, setLoad] = useState<LoadRow | null>(null);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [rules, setRules] = useState<Rule[]>([]);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [form, setForm] = useState<Record<string, string>>({});
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  async function loadData() {
    setLoading(true);
    try {
      const [lv, ev, rv, sv] = await Promise.all([
        supabase.from('loads').select('*').eq('id', id!).maybeSingle(),
        supabase
          .from('events')
          .select('id, event_type, event_value, done_at, done_by')
          .eq('target_type', 'load')
          .eq('target_id', id!)
          .order('done_at', { ascending: false })
          .limit(100),
        supabase.from('rules').select('step_id, next_step_ids, authors'),
        supabase.from('statuses').select('id,key,title'),
      ]);
      if (lv.error) throw lv.error;
      if (ev.error) throw ev.error;
      if (rv.error) throw rv.error;
      if (sv.error) throw sv.error;
      const row = (lv.data as LoadRow) ?? null;
      setLoad(row);
      setEvents((ev.data as EventRow[]) ?? []);
      setRules((rv.data as Rule[]) ?? []);
      setStatuses((sv.data as Status[]) ?? []);
      if (row) {
        const init: Record<string, string> = {};
        editableFields.forEach((f) => {
          const v = row[f.key];
          if (f.type === 'array') {
            init[f.key] = Array.isArray(v) ? v.join(',') : '';
          } else if (v != null && v !== '') {
            init[f.key] = String(v);
          }
        });
        setForm(init);
      }
    } catch {
      setError('Não foi possível carregar a carga.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (id) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const currentRule = useMemo(
    () => (load ? rules.find((r) => r.step_id === load.status_id) ?? null : null),
    [load, rules],
  );

  const allowedNext = useMemo(() => {
    if (!currentRule?.next_step_ids?.length) return [];
    const byId = new Map(statuses.map((s) => [s.id, s]));
    const allowed = currentRule.authors ?? [];
    return currentRule.next_step_ids
      .map((sid) => byId.get(sid))
      .filter((s): s is Status => Boolean(s))
      .filter(() => allowed.some((r) => hasRole(roles, r)) || allowed.includes('creator'));
  }, [currentRule, statuses, roles]);

  async function transition(targetKey: string) {
    setBusy(true);
    setError(null);
    const { error } = await supabase.rpc('transition_load', { p_load: id, p_target: targetKey });
    setBusy(false);
    if (error) {
      setError(error.message || 'Transição não autorizada.');
      return;
    }
    loadData();
  }

  async function onSubmitEdit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const changes: Record<string, unknown> = {};
    editableFields.forEach((f) => {
      const v = form[f.key];
      if (f.type === 'array') {
        const items = (v || '').split(',').map((x) => x.trim()).filter(Boolean);
        changes[f.key] = items;
      } else if (v && v !== '') {
        changes[f.key] = v;
      } else {
        changes[f.key] = null;
      }
    });
    const { error } = await supabase.rpc('update_load', { p_load: id, p_changes: changes });
    setBusy(false);
    if (error) {
      setError(error.message || 'Não foi possível guardar.');
      return;
    }
    loadData();
  }

  async function onSubmitComment(e: FormEvent) {
    e.preventDefault();
    if (!comment.trim()) return;
    setBusy(true);
    setError(null);
    const { error } = await supabase.rpc('add_load_comment', { p_load: id, p_text: comment });
    setBusy(false);
    if (error) {
      setError(error.message || 'Não foi possível comentar.');
      return;
    }
    setComment('');
    loadData();
  }

  function labelEvent(t: string) {
    return eventLabels[t] ?? t;
  }

  if (loading && !load) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!load) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">Carga não encontrada.</p>
        <Button onClick={() => navigate('/loads')}>Voltar às cargas</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button
            onClick={() => navigate('/loads')}
            className="mb-1 cursor-pointer text-sm text-muted-foreground hover:text-foreground"
          >
            <i className="ri-arrow-left-line" /> Cargas
          </button>
          <h1 className="text-2xl md:text-4xl">Carga {load.order_no}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Estado atual:
            <span className="ml-1 inline-flex rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium">
              {titleOf(load.status) ?? '—'}
            </span>
          </p>
        </div>
      </div>

      {error && (
        <p className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}
      {busy && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <i className="ri-loader-4-line animate-spin" /> A guardar…
        </p>
      )}

      <Card title="Ações disponíveis">
        {allowedNext.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Sem ações disponíveis para a tua função neste estado.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {allowedNext.map((s) => (
              <Button key={s.id} onClick={() => transition(s.key)} disabled={busy} variant="secondary">
                <i className="ri-arrow-right-line" />
                {s.title}
              </Button>
            ))}
          </div>
        )}
      </Card>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card title="Editar dados" className="lg:col-span-2">
          <form onSubmit={onSubmitEdit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              {editableFields.map((f) => (
                <label key={f.key} className="space-y-1.5">
                  <span className="text-sm font-medium">{f.label}</span>
                  {f.type === 'time' || f.type === 'date' ? (
                    <input
                      type={f.type}
                      value={form[f.key] ?? ''}
                      onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                      className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  ) : (
                    <input
                      type={f.type === 'number' ? 'number' : 'text'}
                      value={form[f.key] ?? ''}
                      onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                      className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  )}
                </label>
              ))}
            </div>
            <Button type="submit" disabled={busy}>
              <i className="ri-save-3-line" /> Guardar alterações
            </Button>
          </form>
        </Card>

        <div className="space-y-6">
          <Card title="Informação SAP / interna">
            <dl className="space-y-2 text-sm">
              {readonlyFields.map((f) => {
                const v = load[f.key];
                return (
                  <div key={f.key} className="flex items-center justify-between gap-2">
                    <dt className="text-muted-foreground">{f.label}</dt>
                    <dd className="font-medium">{v != null && v !== '' ? String(v) : '—'}</dd>
                  </div>
                );
              })}
            </dl>
          </Card>

          <Card title="Comentário">
            <form onSubmit={onSubmitComment} className="space-y-3">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="Escreve um comentário…"
                className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              <Button type="submit" variant="accent" disabled={busy}>
                <i className="ri-chat-3-line" /> Comentar
              </Button>
            </form>
          </Card>
        </div>
      </div>

      <Card title="Histórico">
        {events.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">Sem eventos registados.</p>
        ) : (
          <ol className="relative ml-3 space-y-4 border-l border-border pl-6">
            {events.map((e) => (
              <li key={e.id}>
                <span className="absolute -left-[7px] mt-1 h-3.5 w-3.5 rounded-full border-2 border-background bg-primary" />
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium">{labelEvent(e.event_type)}</span>
                </div>
                {e.event_value && <p className="mt-0.5 text-sm text-muted-foreground">{e.event_value}</p>}
                <p className="text-xs text-muted-foreground">
                  {new Date(e.done_at).toLocaleString('pt-PT')}
                </p>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}